import React, { useEffect, useState } from "react";
import classnames from "classnames";

// components
import AudioCallModal from "../../../components/AudioCallModal";
import VideoCallModal from "../../../components/VideoCallModal";
import AppSimpleBar from "../../../components/AppSimpleBar";
import Loader from "../../../components/Loader";

import ProfileUser from "./ProfileUser";
import Actions from "./Actions";
import BasicDetails from "./BasicDetails";
import Groups from "./Groups";
import Media from "../../../components/Media";
import AttachedFiles from "../../../components/AttachedFiles";
import Status from "./Status";
import Members from "./Members";
import { useProfile } from "../../../hooks/useProfile";
import { createBookmark, deleteBookmark, getBookmarkContact } from "../../../api";
import { useAuth } from "../../../context/AuthContext";
import { IContact, mapContactToUserTypes } from "../../../types/contacts";
import { useMessages } from "../../../context/MessagesContext";
import { AttachedfileTypes, IContactDetailsTypes, MediaTypes } from "../../../types/profile";
import { getFileIcon, getFileNameFromUrl, ISession } from "../../../types/message";
import LightBox from "../../../components/LightBox";
import SelectUsers from "./SelectUsers";
import { useUsers } from "../../../context/UserContext";
import { useChat } from "../../../context/ChatContext";
import CustomerModal from "../../../components/CustomerModal";
import { useContacts } from "../../../hooks/useContacts";
import VisitModal from "../../../components/VisitModal";
import ServiceModal from "../../../components/ServiceModal";


interface UserProfileProps {
  isOpen: boolean;
  isChannel?: boolean;
  isBlocked: boolean;
  canSendFreeText: boolean;
  onClose: () => void;
}

const UserProfileSidebar = ({
  isOpen,
  isChannel,
  isBlocked,
  canSendFreeText,
  onClose,
}: UserProfileProps) => {

  const [isOpenServiceModal, setIsOpenServiceModal] = useState(false);
  const [isOpenVisitModal, setIsOpenVisitModal] = useState(false);
  const [isOpenCustomerModal, setIsOpenCustomerModal] = useState(false);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);
  const [isOpenAudioModal, setIsOpenAudioModal] = useState(false);
  const [finishSession, setFinishSession] = useState(false);

  const onOpenVideo = () => setIsOpenVideoModal(true);
  const onCloseVideo = () => setIsOpenVideoModal(false);

  const onOpenAudio = () => setIsOpenAudioModal(true);
  const onCloseAudio = () => setIsOpenAudioModal(false);

  const onOpenCustomer = () => setIsOpenCustomerModal(true);
  const onCloseCustomer = () => setIsOpenCustomerModal(false);

  const onOpenVisit = () => setIsOpenVisitModal(true);
  const onCloseVisit = () => setIsOpenVisitModal(false);

  const onOpenService = () => setIsOpenServiceModal(true);
  const onCloseService = () => setIsOpenServiceModal(false);

  const { sessions, contactMarked, favourites, sendCloseSessionMessage, sendTransferSessionMessage, loadSessionsActive } = useMessages();

  const { userList } = useUsers();
  const { clearSelectedChat } = useChat();

  const { contacts, updateContact, contactsVisit, contactsMarked, contactsService } = useContacts();
  const { chatUserDetails } = useProfile();
  const safeUser = chatUserDetails;
  const { user } = useAuth();

  //Marked
  const isMarked = React.useMemo(() => {
    if (!safeUser?.basicDetails?.id) return false;

    // console.log('contactMarked', contactMarked);
    return contactMarked.some(
      c => c.id === safeUser.basicDetails.id
    );
  }, [contactMarked, safeUser?.basicDetails?.id]);

  //Favourite
  const isFavourite = React.useMemo(() => {
    if (!safeUser?.basicDetails?.id) return false;

    // console.log('favourites', favourites);
    return favourites.some(
      c =>
        c.id === safeUser.basicDetails.id
    );
  }, [favourites, safeUser?.basicDetails?.id]);

  //Sessions
  const lastSession = React.useMemo(() => {
    // console.log('[sessions length]', sessions.length)

    if (!user?.id) return undefined;
    if (!safeUser?.basicDetails.id) return undefined;

    // console.log('[sessions user]', user?.id);
    // console.log('[sessions contact]', safeUser?.basicDetails.id);

    // const hasActiveSession = sessions.some(
    //   s => s.idContact === safeUser?.basicDetails.id
    // );

    const result = sessions
      .filter(s =>
        s.idContact === safeUser.basicDetails.id
      )
      .sort((a, b) => b.id - a.id)[0];

    if (result)
      setFinishSession(result.isActive == 1);  // TRUE = pode encerrar

    // console.log('[sessions result]', result);
    return result;
  }, [sessions, user?.id, safeUser?.basicDetails.id]);

  //Imagens
  const fotos = [safeUser?.basicDetails.foto1, safeUser?.basicDetails.foto2, safeUser?.basicDetails.foto3];
  const mediaIMG: MediaTypes = {
    total: fotos.filter(f => f && f.trim() !== "").length,
    list: fotos
      .filter((url): url is string => !!url && url.trim() !== "")
      .map((url, index) => ({
        id: index + 1,
        url,
      })),
  };

  //Files
  const arquivos = [
    safeUser?.basicDetails.arquivo1,
    safeUser?.basicDetails.arquivo2,
  ];

  const attachedFiles: AttachedfileTypes = {
    total: arquivos.filter(Boolean).length,
    list: arquivos
      .filter((file): file is string => !!file && file.trim() !== "")
      .map((file, index) => ({
        id: index + 1,
        fileName: getFileNameFromUrl(file),
        size: "—", // se não tiver o tamanho vindo da API
        downloadUrl: `${file}`,
        icon: getFileIcon(getFileNameFromUrl(file)),
      })),
  };

  const fetchSessionsActive = async () => {
    if (!user?.id) return;
    if (!safeUser?.basicDetails.id) return;

    try {
      //const sessions = await loadSessionsActive();
      const hasActiveSession = sessions.some(
        s => s.idContact === safeUser?.basicDetails.id
      );

      setFinishSession(hasActiveSession);  // TRUE = pode encerrar
    } catch (error) {
      console.error("[fetchSessionsActive] Erro ao buscar sessions:", error);
    }
  };

  // useEffect(() => {
  //   if (!safeUser?.basicDetails.id) return;
  //   fetchSessionsActive();
  // }, [safeUser]);

  const handleToggleMarked = async () => {
    if (!user?.id) return;
    // console.log('[handleToggleFinishMessage] user', user?.id)

    if (!safeUser?.basicDetails.id) return;
    // console.log('[handleToggleFinishMessage] contato', safeUser?.basicDetails.id)

    //comentei pois na API ele encerra sem enviar msg para cliente
    await contactsMarked({
      'idUser': user.id,
      'idContact': safeUser?.basicDetails.id,
      'typeMarked': 1,
    });

  };

  const handleToggleFavourite = async () => {

    if (!user?.id) return;
    if (!safeUser?.basicDetails?.id) return;

    const existing = favourites.find(
      c => c.id === safeUser.basicDetails.id
    );

    if (existing) {
      await deleteBookmark(user.id, safeUser.basicDetails.id);
    } else {
      await createBookmark({
        idUser: user.id,
        idContact: safeUser.basicDetails.id,
      });
    }
  };

  const handleToggleFinishMessage = async () => {

    if (!user?.id) return;
    // console.log('[handleToggleFinishMessage] user', user?.id)

    if (!safeUser?.basicDetails.id) return;
    // console.log('[handleToggleFinishMessage] contato', safeUser?.basicDetails.id)

    //comentei pois na API ele encerra sem enviar msg para cliente
    // if (isBlocked) return;
    // if (!canSendFreeText) return;   
    await sendCloseSessionMessage({
      'idUser': lastSession == undefined ? 3 : user.id,
      'idContact': safeUser?.basicDetails.id
    });
    setFinishSession(false);
  };

  const handleTransferSession = async (idUserNew: number) => {

    if (!user?.id) return;
    if (!safeUser?.basicDetails.id) return;

    const sessionRobot = sessions
      .filter(s => s.idContact === safeUser?.basicDetails?.id)
      .sort((a, b) => b.id - a.id)[0];

    const userSessionId =
      sessionRobot && (sessionRobot.isAiActive === 1 || sessionRobot.isActive)
        ? sessionRobot.idUser
        : user?.id ?? 0;


    console.log('[handleTransferSession] Atendente ', idUserNew)
    await sendTransferSessionMessage({ 'idUser': userSessionId, 'idUserNew': idUserNew, 'idContact': safeUser?.basicDetails.id });
    onClose();
    clearSelectedChat()
  };

  const handleCustomerConfirm = async (data: any) => {

    if (!user?.id) return;
    if (!safeUser?.basicDetails.id) return;

    const contactEdit = contacts.find(c => c.id === safeUser?.basicDetails.id);
    if (!contactEdit) return;

    contactEdit.idCliente = data.idCliente;
    contactEdit.cpf = data.cpf;
    contactEdit.cpfCnpj = data.cpf;

    const edited = {
      ...contactEdit,
      idCliente: data.idCliente,
      cpf: data.cpf,
      cpfCnpj: data.cpf,
    };

    const contactEdited = await updateContact(edited);
    console.log('[handleCustomerConfirm] ContactEdit ', contactEdited)

    // const mappedContact = mapContactToUserTypes(contactEdited!!);
    // console.log('[handleCustomerConfirm] ContactEdit ', mappedContact)

    // const newDetails: IContactDetailsTypes = {
    //   basicDetails: {
    //     id: mappedContact.id,
    //     fullName: mappedContact.name,
    //     firstName: mappedContact.name,
    //     lastName: mappedContact.name,
    //     title: mappedContact.name,
    //     description: mappedContact.id + " - " + mappedContact.lastMessageText,
    //     email: mappedContact.email, // "admin@themesbrand.com",
    //     location: "Brasil, BRA",
    //     avatar: '',
    //     cover: "",

    //     //Dados Cliente (Finance)
    //     idCliente: mappedContact.idCliente,
    //     idEnterprise: mappedContact.idEnterprise,
    //     name: mappedContact.fullName,
    //     cpfCnpj: mappedContact.cpfCnpj,
    //     address: mappedContact.address,
    //     situacao: mappedContact.situacao,
    //     obs: mappedContact.obs,
    //     obsAtendimento: mappedContact.obsAtendimento,
    //     foto1: mappedContact.foto1,
    //     foto2: mappedContact.foto2,
    //     foto3: mappedContact.foto3,
    //     arquivo1: mappedContact.arquivo1,
    //     arquivo2: mappedContact.arquivo2,

    //     unreadCount: mappedContact.unreadCount,
    //     lastMessage: mappedContact.lastMessageText,
    //     lastMessageTime: mappedContact.lastMessageTime,
    //     blockedMessage: mappedContact.blockedMessage,

    //   },
    //   media: { total: 0, list: [] },
    //   attachedFiles: { total: 0, list: [] },
    // };

    // setChatUserDetails(newDetails);

    onCloseCustomer();
  };

  const handleToggleVisit = async (data: any) => {
    try {
      if (!user?.id) return;
      // console.log("idUserLogado", user.id);
      if (!safeUser?.basicDetails.id) return;
      // console.log("idContact", safeUser?.basicDetails.id);
      // console.log("handleToggleVisit", data);
      const response = await contactsVisit({
        'idUser': user.id,
        'idContact': safeUser?.basicDetails.id,
        'observation': data.message,
        'dateVisit': data.dateVisit
      });
      onCloseVisit();

    } catch (error) {
      console.error("[Leftbar] Erro ao buscar contatos:", error);
    }
  };

  const handleToggleService = async (data: any) => {
    try {
      if (!user?.id) return;
      // console.log("idUserLogado", user.id);
      if (!safeUser?.basicDetails.id) return;
      // console.log("idContact", safeUser?.basicDetails.id);
      console.log("handleToggleService", data);

      const response = await contactsService({
        'idUser': user.id,
        'idContact': safeUser?.basicDetails.id,
        'observation': data.message,
        'marked': data.confirm
      });

      onCloseService();
      clearSelectedChat();

    } catch (error) {
      console.error("[Leftbar] Erro ao buscar contatos:", error);
    }
  };

  return (
    <div
      className={classnames("user-profile-sidebar", {
        "d-block": isOpen,
      })}
    >
      <div className="position-relative">
        {!safeUser && <Loader />}

        {safeUser && (
          <>
            <ProfileUser
              isBlocked={isBlocked}
              canSendFreeText={canSendFreeText}
              onCloseUserDetails={onClose}
              chatUserDetails={safeUser.basicDetails}
              onOpenVideo={onOpenVideo}
              onOpenAudio={onOpenAudio}
            />

            <AppSimpleBar className="p-4 user-profile-desc">
              <Actions
                isFavourite={isFavourite}
                isMarked={isMarked}
                isSession={finishSession}
                chatUserDetails={safeUser}
                onToggleMessageClose={handleToggleFinishMessage}
                onOpenVideo={onOpenVideo}
                onOpenAudio={onOpenAudio}
                onOpenVisit={onOpenVisit}
                onToggleMarked={handleToggleMarked}
                onOpenService={onOpenService}
                onToggleFavourite={handleToggleFavourite}
                onToggleArchive={() => alert('Arquivar')}
              />

              <Status session={lastSession} chatUserDetails={safeUser.basicDetails} />

              {!isChannel ? (
                <>
                  <BasicDetails onOpenCustomer={onOpenCustomer} chatUserDetails={safeUser.basicDetails} />
                  <hr className="my-4" />

                  <SelectUsers
                    selectedUserId={lastSession?.idUser || 0}
                    users={userList}
                    onSelectUser={handleTransferSession}
                  />
                  <hr className="my-4" />
                </>
              ) : (
                <>
                  <Members chatUserDetails={safeUser.basicDetails} />
                  <hr className="my-4" />
                </>
              )}

              {/* <Media media={safeUser.media ?? []} limit={3} /> */}
              <Media media={mediaIMG ?? []} limit={3} />
              <hr className="my-4" />

              {/* <AttachedFiles attachedFiles={safeUser.attachedFiles ?? []} /> */}
              <AttachedFiles attachedFiles={attachedFiles ?? []} />
            </AppSimpleBar>


            {/* <LightBox isOpen={isOpen} images={mediaIMG as any} onClose={onClose} defaultIdx={0} /> */}


            {isOpenCustomerModal && (
              <CustomerModal
                isOpen={isOpenCustomerModal}
                onClose={onCloseCustomer}
                chatUserDetails={safeUser.basicDetails}
                onConfirm={handleCustomerConfirm}
              />
            )}

            {isOpenVisitModal && (
              <VisitModal
                isOpen={isOpenVisitModal}
                onClose={onCloseVisit}
                chatUserDetails={chatUserDetails.basicDetails}
                onConfirm={handleToggleVisit}
              // user={safeUser.media}
              />
            )}

            {isOpenServiceModal && (
              <ServiceModal
                isOpen={isOpenServiceModal}
                onClose={onCloseService}
                chatUserDetails={chatUserDetails.basicDetails}
                onConfirm={handleToggleService}
              // user={safeUser.media}
              />
            )}

            {isOpenAudioModal && (
              <AudioCallModal
                isOpen={isOpenAudioModal}
                onClose={onCloseAudio}
                user={null}
              // user={safeUser.media}
              />
            )}

            {isOpenVideoModal && (
              <VideoCallModal
                isOpen={isOpenVideoModal}
                onClose={onCloseVideo}
                user={null}
              // user={safeUser}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfileSidebar;
